/**
 * Per-device ACTIVE-alarm severity for the SLD mini cards.
 *
 * WHY THIS EXISTS (djin-cloud#684)
 *
 * The mini cards used to badge FAULT straight off the telemetry `faulted`
 * flag. That flag is not an operator-facing fault: genieos's
 * `applyAlarmFaultOverride` (state_publisher.go:462) raises it for any device
 * alarm at or above SeverityUrgent **regardless of acknowledgement**, so a
 * site whose alarms have all been acked paints a red FAULT card forever
 * (Hondo, 7 acked-and-open rows, 0 unacked). It also carries no severity
 * granularity — Turner's single active *warning* rendered as a full-red FAULT
 * card with its numerics blanked.
 *
 * So the badge is driven from the cloud's alarm rows instead, and "active"
 * here means the strict reading: **uncleared AND unacknowledged**. That is
 * deliberately narrower than the `alarms.active_alarms` table's own sense of
 * active (`cleared_at IS NULL`, acked included) and narrower than what
 * `useSiteAlarms({ statuses: ['active'] })` returns — an acked row is one the
 * operator has already taken ownership of, and it belongs on the Alarms tab,
 * not on the diagram.
 *
 * DTC rows are excluded on purpose. `gen.dtc.code` is a single warning-severity
 * row carrying the whole vendor diagnostic-code list, and it is long-lived on
 * healthy engines — badging it would paint most generator cards yellow
 * permanently and drown the signal this change exists to surface. DTCs remain
 * fully visible in the device detail panel's own DTC section.
 */
import type { ActiveAlarm } from '@/types/alarm.types';
import { isDtcCondition } from './dtc';

/** Highest active-alarm severity for a device; `null` = nothing active. */
export type DeviceAlarmSeverity = 'critical' | 'warning';

/**
 * An SLD node's `data.deviceId` is a telemetry join key, which for a BESS tile
 * is a BLOCK id (Hondo's BESS node carries `ES-E3E0`) while its alarm rows
 * carry `device_id` `BMS-001` / `PCS-001` with `block_id` `ES-E3E0`. So every
 * row is indexed under both identities — mirrors `bucketDeviceConditions` in
 * hooks/useDeviceConditions.ts, which solves the same join for the panel. For
 * a generator `block_id === device_id`, so the second key is a no-op there.
 *
 * Critical always wins over warning for a given key.
 */
export function buildDeviceAlarmSeverityMap(
  rows: readonly ActiveAlarm[],
): Map<string, DeviceAlarmSeverity> {
  const map = new Map<string, DeviceAlarmSeverity>();

  for (const row of rows) {
    if (row.cleared_at) continue;
    if (row.acked_at) continue;
    if (isDtcCondition(row.condition_key)) continue;
    if (row.severity !== 'critical' && row.severity !== 'warning') continue;

    for (const key of [row.device_id, row.block_id]) {
      if (!key) continue;
      if (row.severity === 'critical' || !map.has(key)) {
        map.set(key, row.severity);
      }
    }
  }

  return map;
}
